// How a project code reads everywhere in the app (Chris, 2026-10-14): "AN001 · Andela", the code and
// its client from the SharePoint Projects list. One fetch of the list per session, shared by every
// screen; a code the list doesn't have, or a caller who can't read the list, shows the bare code.

import { useEffect, useMemo, useState } from "react";
import { api, type CodesResponse } from "./api";
import { codeColors, fallbackColor, type CodeColor } from "./codeColors";

type Codes = Map<string, { client: string; retired?: boolean; billable?: boolean }>;
let cache: Promise<Codes> | null = null;

function load(): Promise<Codes> {
  cache ??= api<CodesResponse>("/project-codes")
    .then((r) => new Map(r.codes.map((c) => [c.code, { client: c.client, retired: c.retired, billable: c.billable }])))
    .catch(() => {
      cache = null;
      return new Map();
    });
  return cache;
}

/** After the Projects list is refreshed, so new codes pick up their clients. */
export const forgetCodeLabels = () => {
  cache = null;
};

/** "AN001 · Andela", or "AN001" when the client isn't known. */
export const formatCode = (code: string, client: string | undefined) => (client ? `${code} · ${client}` : code);

/**
 * The label and color for each code, the sorted codes pickers offer with their labels, and every code known. A
 * retired code (closed before 2026) still labels old records but isn't offered (Chris, 2026-10-28).
 */
export function useCodeLabels(): { label: (code: string) => string; color: (code: string) => CodeColor; options: { code: string; label: string }[]; known: ReadonlySet<string>; ready: boolean } {
  const [clients, setClients] = useState<Codes | null>(null);
  useEffect(() => {
    let live = true;
    void load().then((m) => live && setClients(m));
    return () => {
      live = false;
    };
  }, []);
  const map: Codes = clients ?? new Map();
  const colors = useMemo(() => codeColors([...map].map(([code, c]) => ({ code, billable: c.billable }))), [clients]);
  return {
    color: (code: string) => colors.get(code) ?? fallbackColor(code),
    label: (code: string) => (code ? formatCode(code, map.get(code)?.client) : code),
    options: [...map.entries()]
      .filter(([, c]) => !c.retired)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([code, c]) => ({ code, label: formatCode(code, c.client) })),
    known: new Set(map.keys()),
    ready: clients !== null,
  };
}
